/**
 * Org logo placement, derived in ONE place.
 *
 * The customer logo shows in either the left navigation sidebar or the top bar, never both, and the
 * Antaia mark sits in either the top bar or a small badge at the foot of the left nav — again never
 * both, and never neither. Three components render pieces of that (`Sidebar`'s header and footer,
 * `Navbar`) and each of them used to re-derive the rules from `orgData` itself, which is how the
 * Antaia mark could go missing entirely (top-bar mode, "Hide Antaia Logo" ticked, and no customer
 * logo to take its place left a top bar containing only the hamburger).
 *
 * These two functions are pure so the invariants are unit-testable; `useOrgLogo` is the hook that
 * feeds them and resolves the image itself.
 *
 * Standalone file, not re-exported through `utils/index.js`: that barrel contains JSX and vitest
 * cannot parse it (see frontend/CLAUDE.md → "Vitest imports from src/utils"). Import from
 * "/src/utils/logoStyle" in tests.
 */

/**
 * Read the org's stored `logoStyle` with defaults applied. Every field is nullable server-side and
 * the column itself may be null; unset always means the behaviour the app had before these settings
 * existed — sidebar, centered, no badge — so an org that has never been configured looks untouched.
 *
 * @param {?Object} orgData the org as returned by GET /administration/organization/{id}
 * @returns {{location: "left"|"top", alignment: "center"|"left", showAntaiaBranding: boolean}}
 */
export function resolveLogoStyle(orgData) {
  const stored = orgData?.logoStyle;
  return {
    // Anything unrecognised falls back rather than throwing: this is display config an admin types
    // into a free-form select, and a bad value should degrade to the default, not blank the chrome.
    location: stored?.logoLocation === "top" ? "top" : "left",
    alignment: stored?.logoAlignment === "left" ? "left" : "center",
    showAntaiaBranding: !!stored?.showAntaiaBranding,
  };
}

/**
 * Turn the settings plus "is there actually a logo to show" into the four render decisions.
 *
 * `showAntaiaBranding` is named for the badge it turns on, but its visible effect is to REMOVE the
 * Antaia logo from the top bar so the customer logo has that spot to itself. So it may only take
 * effect when there IS a customer logo — otherwise it empties the top bar. That `hasCustomerLogo`
 * term is the whole point of this function.
 *
 * `showAntaiaInTopBar` and `showAntaiaBadgeInSidebarFooter` are exact complements by construction,
 * which is what stops the mark appearing twice or not at all. Do not re-derive either at a call site.
 *
 * Layout state (collapsed sidebar, breakpoints) is deliberately NOT an input — it differs per
 * component, so each site ANDs in its own.
 *
 * @param {{location: string, showAntaiaBranding: boolean}} style from resolveLogoStyle
 * @param {boolean} hasCustomerLogo whether a logo image actually resolved
 */
export function resolveLogoPlacement({ location, showAntaiaBranding }, hasCustomerLogo) {
  const inTopBar = location === "top";
  const antaiaMovesToSidebar = inTopBar && !!showAntaiaBranding && !!hasCustomerLogo;
  return {
    showCustomerLogoInSidebar: !inTopBar && !!hasCustomerLogo,
    showCustomerLogoInTopBar: inTopBar && !!hasCustomerLogo,
    showAntaiaInTopBar: !antaiaMovesToSidebar,
    showAntaiaBadgeInSidebarFooter: antaiaMovesToSidebar,
  };
}
